import { Injectable } from '@angular/core';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../../../core/reducers';
import {
  DrugGroupActionTypes,
  DrugGroupsPageRequested,
  DrugGroupsPageLoaded,
  DrugGroupCreated,
  DrugGroupDeleted,
  DrugGroupUpdated,
  DrugGroupOnServerCreated,
  DrugGroupsActionToggleLoading,
  DrugGroupsPageToggleLoading,
  ThrowDrugGroupError,
  FireDrugGroupSuccess,
  DrugGroupOnServerUpdated,
  DrugGroupsPageCancelled,
  DrugGroupOnServerDeleted,
} from '../_actions/drug-group.actions';
import { Router } from '@angular/router';
import { DrugGroupService } from '../_services';
import { AuthService } from '../../auth/_services';

@Injectable()
export class DrugGroupEffects {
  showPageLoadingDistpatcher = new DrugGroupsPageToggleLoading({ isLoading: true });
  hidePageLoadingDistpatcher = new DrugGroupsPageToggleLoading({ isLoading: false });
  showActionLoadingDistpatcher = new DrugGroupsActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new DrugGroupsActionToggleLoading({ isLoading: false });

  @Effect()
  loadDrugGroupsPage$ = this.actions$.pipe(
    ofType<DrugGroupsPageRequested>(DrugGroupActionTypes.DrugGroupsPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.drugGroup.findDrugGroups(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin([requestToServer, lastQuery]).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0] && response[0].error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new DrugGroupsPageCancelled();
            } else {
              this.store.dispatch(new ThrowDrugGroupError({ error: response[0].error.error }));
              return new DrugGroupsPageCancelled();
            }
          } else {
            const result = response[0];
            return new DrugGroupsPageLoaded({
              drugGroups: result.data,
              totalCount: result.count,
              page: response[1],
            });
          }
        }),
      );
    }),
  );

  @Effect()
  deleteDrugGroup$ = this.actions$.pipe(
    ofType<DrugGroupOnServerDeleted>(DrugGroupActionTypes.DrugGroupOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.drugGroup.deleteDrugGroup(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowDrugGroupError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireDrugGroupSuccess({ action_type: DrugGroupActionTypes.DrugGroupDeleted }),
            );
            return new DrugGroupDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updateDrugGroup$ = this.actions$.pipe(
    ofType<DrugGroupOnServerUpdated>(DrugGroupActionTypes.DrugGroupOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.drugGroup.updateDrugGroup(payload.drugGroup).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowDrugGroupError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireDrugGroupSuccess({ action_type: DrugGroupActionTypes.DrugGroupUpdated }),
            );
            return new DrugGroupUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  createDrugGroup$ = this.actions$.pipe(
    ofType<DrugGroupOnServerCreated>(DrugGroupActionTypes.DrugGroupOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.drugGroup.createDrugGroup(payload.drugGroup).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowDrugGroupError({ error: res.error });
          } else {
            this.store.dispatch(
              new FireDrugGroupSuccess({ action_type: DrugGroupActionTypes.DrugGroupCreated }),
            );
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new DrugGroupCreated({ drugGroup: res });
          }
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private drugGroup: DrugGroupService,
    private store: Store<AppState>,
    private router: Router,
    private authService: AuthService,
  ) {}
}
